import React, { useState, useMemo, useCallback } from 'react';
import WrestlerCard, { WrestlerCardStyles } from './WrestlerCard';
import { getWrestlerName, wrestlerMatchesSearch, sortWrestlersByName } from './wrestlerUtils';

// ── Constants ──────────────────────────────────────────────────────────
const BRAND_LOGOS_MAP = {
  'Raw':        process.env.PUBLIC_URL + '/brands/logos/RAW25_LOGO.png',
  'SmackDown':  process.env.PUBLIC_URL + '/brands/logos/SMACKDOWN25_LOGO.png',
  'NXT':        process.env.PUBLIC_URL + '/brands/logos/NXT_LOGO.png',
  'NXT 2.0':   process.env.PUBLIC_URL + '/brands/logos/NXT20_LOGO.png',
  'WCW':        process.env.PUBLIC_URL + '/brands/logos/WCW_LOGO.png',
  'ECW':        process.env.PUBLIC_URL + '/brands/logos/ECW_LOGO.png',
  'NXT Mutiny': process.env.PUBLIC_URL + '/brands/logos/NXTMUTINY_LOGO.png',
};
const getBrandLogo = (name) => BRAND_LOGOS_MAP[name] || null;

const BRAND_COLORS_MAP = {
  'Raw':        '#c0392b',
  'SmackDown':  '#1a6bb5',
  'NXT':        '#6c3483',
  'NXT 2.0':   '#148f77',
  'WCW':        '#b7950b',
  'ECW':        '#ba4a00',
  'NXT Mutiny': '#212f3c',
};
const getBrandColor = (name) => BRAND_COLORS_MAP[name] || null;

const css = `
  * { box-sizing: border-box; }
  html { font-size: 133%; }
  .su-pick { transition: transform 0.15s, filter 0.15s; cursor: pointer; }
  .su-pick:hover:not(.su-disabled) { transform: translateY(-3px); filter: brightness(1.1); }
  .su-pick.su-disabled { opacity: 0.35; cursor: not-allowed; pointer-events: none; }
  .su-new-glow > div { box-shadow: 0 0 10px 2px rgba(46,204,113,0.4) !important; border-color: rgba(46,204,113,0.5) !important; }
  .su-deal-card { border: 1px solid rgba(255,255,255,0.08); border-radius: 8px; padding: 10px 12px; background: rgba(255,255,255,0.03); cursor: pointer; transition: border-color 0.15s, background 0.15s; }
  .su-deal-card:hover { border-color: rgba(255,255,255,0.18); background: rgba(255,255,255,0.05); }
  .su-deal-card.su-active { border-color: rgba(212,175,55,0.6); background: rgba(212,175,55,0.06); }
  .su-dd { position: relative; user-select: none; }
  .su-dd-btn { display: flex; align-items: center; gap: 10px; padding: 7px 12px; border-radius: 6px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.15); cursor: pointer; min-width: 180px; }
  .su-dd-menu { position: absolute; top: calc(100% + 4px); left: 0; min-width: 100%; background: rgba(10,8,6,0.98); border: 1px solid rgba(255,255,255,0.12); border-radius: 8px; z-index: 300; overflow: hidden; box-shadow: 0 8px 24px rgba(0,0,0,0.5); }
  .su-dd-item { display: flex; align-items: center; gap: 10px; padding: 9px 14px; cursor: pointer; transition: background 0.1s; }
  .su-dd-item:hover { background: rgba(255,255,255,0.07); }
  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 2px; }
`;

const S = {
  anton: { fontFamily: "'Anton', sans-serif" },
  mono:  { fontFamily: "'Monda', sans-serif" },
  label: { fontFamily: "'Monda', sans-serif", fontSize: '0.4rem', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase' },
};

// ── Helpers ────────────────────────────────────────────────────────────
// logoUrl = custom brand Firebase Storage URL (takes priority over BRAND_LOGOS_MAP)
const BrandLogo = ({ brand, color, height = 22, logoUrl }) => {
  const logo = logoUrl || getBrandLogo(brand);
  return logo
    ? <img loading="lazy" src={logo} alt={brand} style={{ height: height + 'px', objectFit: 'contain', flexShrink: 0 }} onError={function(e){ e.target.style.display = 'none'; }} />
    : <span style={{ ...S.anton, fontSize: '0.6rem', color: color || '#d4af37', letterSpacing: '1px' }}>{brand || '?'}</span>;
};

const BrandPill = ({ brand, color, logoUrl }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 12px', borderRadius: '6px', background: (color || '#888') + '14', border: '1px solid ' + (color || '#888') + '55' }}>
    <BrandLogo brand={brand} color={color} height={22} logoUrl={logoUrl} />
  </div>
);

function BrandDropdown({ value, options, onChange, placeholder, rivalsList }) {
  const [open, setOpen] = useState(false);
  const rivalObj = (rivalsList || []).find(function(r){ return r.brand === value; });
  const selColor = rivalObj ? rivalObj.color : null;
  const selLogoUrl = rivalObj ? (rivalObj.brandLogo || null) : null;
  return (
    <div className="su-dd">
      <div className="su-dd-btn" onClick={function(){ setOpen(function(v){ return !v; }); }}>
        {value
          ? <BrandLogo brand={value} color={selColor} height={20} logoUrl={selLogoUrl} />
          : <span style={{ ...S.mono, fontSize: '0.44rem', color: 'rgba(255,255,255,0.4)' }}>{placeholder || '— Select —'}</span>
        }
        <span style={{ marginLeft: 'auto', color: 'rgba(255,255,255,0.35)', fontSize: '0.5rem' }}>▾</span>
      </div>
      {open && (
        <div className="su-dd-menu">
          {options.map(function(brand) {
            const rObj = (rivalsList || []).find(function(r){ return r.brand === brand; });
            const rColor = rObj ? rObj.color : null;
            const rLogoUrl = rObj ? (rObj.brandLogo || null) : null;
            return (
              <div key={brand} className="su-dd-item" onClick={function(){ onChange(brand); setOpen(false); }}>
                <BrandLogo brand={brand} color={rColor} height={20} logoUrl={rLogoUrl} />
                <span style={{ ...S.mono, fontSize: '0.44rem', color: rColor || 'rgba(255,255,255,0.7)', letterSpacing: '0.5px' }}>{brand}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const SearchBar = ({ value, onChange }) => (
  <input type="text" value={value} onChange={function(e){ onChange(e.target.value); }} placeholder="Search superstar…"
    style={{ width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', padding: '7px 12px', color: '#fff', fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', outline: 'none', letterSpacing: '0.5px' }}
  />
);

// ── Picker column ──────────────────────────────────────────────────────
const PickerColumn = ({ label, brandName, accentColor, wrestlers, picks, onToggle, search, onSearch, maxPicks, emptyMsg, logoUrl }) => {
  const logo = logoUrl || getBrandLogo(brandName);
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
      <div style={{ padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.05)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(0,0,0,0.3)', borderLeft: '3px solid ' + (accentColor || 'transparent') }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {logo && <img loading="lazy" src={logo} alt={brandName} style={{ height: '20px', objectFit: 'contain' }} onError={function(e){ e.target.style.display='none'; }} />}
          <span style={{ ...S.anton, fontSize: '0.7rem', letterSpacing: '2px', color: accentColor || '#fff' }}>{label}</span>
        </div>
        <span style={{ ...S.mono, fontSize: '0.38rem', letterSpacing: '1px', color: picks.length > 0 ? accentColor : 'rgba(255,255,255,0.3)', background: picks.length > 0 ? (accentColor || '#888') + '18' : 'transparent', border: '1px solid ' + (picks.length > 0 ? (accentColor || '#888') + '44' : 'transparent'), padding: '2px 8px', borderRadius: '4px' }}>
          {picks.length}/{maxPicks || 3} SELECTED
        </span>
      </div>
      <div style={{ padding: '10px 16px', flexShrink: 0 }}>
        <SearchBar value={search} onChange={onSearch} />
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 16px 16px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignContent: 'flex-start' }}>
        {wrestlers.map(function(w) {
          var wid = String(w.wrestler_id);
          var isSelected = !!picks.find(function(x){ return String(x.wrestler_id) === wid; });
          var isDisabled = !isSelected && picks.length >= (maxPicks || 3);
          return (
            <div key={wid} className={'su-pick' + (isDisabled ? ' su-disabled' : '')} onClick={isDisabled ? undefined : function(){ onToggle(w); }} style={{ position: 'relative' }}>
              {isSelected && (
                <div style={{ position: 'absolute', top: 4, right: 4, zIndex: 10, width: 18, height: 18, borderRadius: '50%', background: accentColor || '#2ecc71', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.55rem', color: '#fff', fontWeight: 'bold' }}>✓</div>
              )}
              <WrestlerCard wrestler={w} variant="preview" size={{ width: 110, height: 230, renderHeight: 158 }} brandColor={accentColor || '#d4af37'} hideXP={true} hideStats={false} hasSynergy={false} isChampionGlow={false} showActionButtons={false} />
            </div>
          );
        })}
        {wrestlers.length === 0 && (
          <div style={{ width: '100%', paddingTop: '40px', textAlign: 'center', ...S.mono, fontSize: '0.5rem', color: 'rgba(255,255,255,0.2)', letterSpacing: '1px' }}>
            {emptyMsg || 'No superstars available'}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Recap card ─────────────────────────────────────────────────────────
const RecapCard = ({ wrestler, brandName, brandLogoUrl, badgeColor, brandColor, direction }) => {
  const logo = brandLogoUrl || getBrandLogo(brandName);
  const arrow = direction === 'in' ? '↓' : '↑';
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      {/* Brand badge — logo if available, text fallback */}
      <div style={{ position: 'absolute', top: 6, left: 6, zIndex: 10, display: 'flex', alignItems: 'center', gap: '4px', background: (badgeColor || '#888') + '28', border: '1px solid ' + (badgeColor || '#888') + '55', borderRadius: '4px', padding: logo ? '3px 6px' : '2px 6px', maxWidth: '80px' }}>
        <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.3rem', color: badgeColor || '#fff', flexShrink: 0 }}>{arrow}</span>
        {logo
          ? <img loading="lazy" src={logo} alt={brandName} style={{ height: '14px', objectFit: 'contain', maxWidth: '56px' }} onError={function(e){ e.target.style.display='none'; }} />
          : <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.3rem', letterSpacing: '0.5px', color: badgeColor || '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{brandName}</span>
        }
      </div>
      <WrestlerCard wrestler={wrestler} variant="preview" size={{ width: 120, height: 248, renderHeight: 175 }} brandColor={brandColor || '#d4af37'} hideXP={true} hideStats={false} hasSynergy={false} isChampionGlow={false} showActionButtons={false} />
    </div>
  );
};

// ── Deal summary card (left panel) ─────────────────────────────────────
const DealCard = ({ deal, index, isActive, onClick, onDelete, rivalsList }) => {
  const rivalObj = (rivalsList || []).find(function(r){ return r.brand === deal.brand; });
  const rColor = rivalObj ? (rivalObj.color || rivalObj.brandColor || getBrandColor(deal.brand) || '#888') : (getBrandColor(deal.brand) || '#888');
  const logo = (rivalObj && rivalObj.brandLogo) || getBrandLogo(deal.brand);
  const cashDelta = Number(deal.cashIn || 0) - Number(deal.cashOut || 0);
  return (
    <div className={'su-deal-card' + (isActive ? ' su-active' : '')} onClick={onClick}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '7px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
          <span style={{ ...S.mono, fontSize: '0.34rem', letterSpacing: '1px', color: 'rgba(255,255,255,0.3)' }}>#{index + 1}</span>
          {logo
            ? <img loading="lazy" src={logo} alt={deal.brand} style={{ height: '16px', objectFit: 'contain' }} onError={function(e){ e.target.style.display='none'; }} />
            : <span style={{ ...S.anton, fontSize: '0.55rem', color: rColor, letterSpacing: '1px' }}>{deal.brand}</span>
          }
        </div>
        <button onClick={function(e){ e.stopPropagation(); onDelete(); }}
          style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.25)', cursor: 'pointer', padding: '2px 5px', borderRadius: '4px', fontSize: '0.5rem', lineHeight: 1 }}
          title="Delete deal">✕</button>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
        {(deal.picks_in || []).map(function(w) {
          return (
            <span key={w.wrestler_id} style={{ ...S.mono, fontSize: '0.35rem', letterSpacing: '0.5px', color: '#2ecc71', background: 'rgba(46,204,113,0.1)', border: '1px solid rgba(46,204,113,0.25)', borderRadius: '3px', padding: '2px 6px' }}>
              ↓ {getWrestlerName(w, 'display')}
            </span>
          );
        })}
        {(deal.picks_out || []).map(function(w) {
          return (
            <span key={w.wrestler_id} style={{ ...S.mono, fontSize: '0.35rem', letterSpacing: '0.5px', color: '#e74c3c', background: 'rgba(231,76,60,0.1)', border: '1px solid rgba(231,76,60,0.25)', borderRadius: '3px', padding: '2px 6px' }}>
              ↑ {getWrestlerName(w, 'display')}
            </span>
          );
        })}
        {(deal.picks_in || []).length === 0 && (deal.picks_out || []).length === 0 && (
          <span style={{ ...S.mono, fontSize: '0.35rem', color: 'rgba(255,255,255,0.2)', letterSpacing: '0.5px' }}>No wrestlers selected</span>
        )}
        {cashDelta !== 0 && (
          <span style={{ ...S.mono, fontSize: '0.35rem', color: cashDelta > 0 ? '#2ecc71' : '#e74c3c', marginLeft: '2px' }}>
            {cashDelta > 0 ? '+' : ''}{cashDelta > 0 ? '+$' : '-$'}{Math.abs(cashDelta).toLocaleString('en-US')}
          </span>
        )}
      </div>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════
// ── MAIN COMPONENT ───────────────────────────────────────────────────
// ═════════════════════════════════════════════════════════════════════
export default function ShakeUpFlow({ gameData, onComplete, onSkip }) {
  const brandColor        = (gameData && gameData.brandColor) || '#d4af37';
  const myBrand           = (gameData && gameData.brand) || 'My Brand';
  const roster            = useMemo(function(){ return (gameData && gameData.roster) || []; }, [gameData]);
  const budget            = (gameData && gameData.budget) || 0;
  const rivals            = useMemo(function(){ return ((gameData && gameData.rivals) || []).filter(Boolean); }, [gameData]);
  const officialWrestlers = useMemo(function(){ return (gameData && gameData._officialWrestlers) || []; }, [gameData]);

  // step: 'deals' | 'recap'
  const [step, setStep] = useState('deals');

  // ── Deals list: array of { id, brand, picks_in, picks_out, cashIn, cashOut }
  const [deals, setDeals] = useState([]);

  // ── Currently editing deal id (null = new draft, string = editing existing)
  const [editingId, setEditingId] = useState(null);

  // ── Draft state for the editor
  const emptyDraft = { brand: '', picks_in: [], picks_out: [], cashIn: '', cashOut: '' };
  const [draft, setDraft] = useState(emptyDraft);

  // ── Search states
  const [searchLeft,  setSearchLeft]  = useState('');
  const [searchRight, setSearchRight] = useState('');

  // ── Whether the editor panel is open
  const editorOpen = editingId !== null || draft.brand !== '' || deals.length === 0;

  // ── Roster after applying all SAVED deals (not the draft) ──────────
  const rosterAfterDeals = useMemo(function() {
    var r = roster.slice();
    deals.forEach(function(d) {
      (d.picks_out || []).forEach(function(w) {
        r = r.filter(function(x){ return String(x.wrestler_id) !== String(w.wrestler_id); });
      });
      (d.picks_in || []).forEach(function(w) {
        if (!r.find(function(x){ return String(x.wrestler_id) === String(w.wrestler_id); })) {
          r.push({ ...w, _newArrival: true });
        }
      });
    });
    return r;
  }, [roster, deals]);

  // ── Roster for the CURRENT editor: apply all deals EXCEPT the one being edited
  const rosterForEditor = useMemo(function() {
    var r = roster.slice();
    deals.forEach(function(d) {
      if (d.id === editingId) return; // skip the one being edited
      (d.picks_out || []).forEach(function(w) {
        r = r.filter(function(x){ return String(x.wrestler_id) !== String(w.wrestler_id); });
      });
      (d.picks_in || []).forEach(function(w) {
        if (!r.find(function(x){ return String(x.wrestler_id) === String(w.wrestler_id); })) {
          r.push({ ...w });
        }
      });
    });
    return r;
  }, [roster, deals, editingId]);

  // Pool for editor: everything NOT in rosterForEditor
  const rosterForEditorIds = useMemo(function(){
    return rosterForEditor.map(function(w){ return String(w.wrestler_id); });
  }, [rosterForEditor]);

  const poolForEditor = useMemo(function(){
    return officialWrestlers.filter(function(w){ return !rosterForEditorIds.includes(String(w.wrestler_id)); });
  }, [officialWrestlers, rosterForEditorIds]);

  // ── Filtered pools ─────────────────────────────────────────────────
  const filteredLeft = useMemo(function(){
    if (!searchLeft) return poolForEditor;
    return poolForEditor.filter(function(w){ return wrestlerMatchesSearch(w, searchLeft); });
  }, [poolForEditor, searchLeft]);

  const filteredRight = useMemo(function(){
    if (!searchRight) return rosterForEditor;
    return rosterForEditor.filter(function(w){ return wrestlerMatchesSearch(w, searchRight); });
  }, [rosterForEditor, searchRight]);

  // ── Editor helpers ─────────────────────────────────────────────────
  function openNewDeal() {
    setEditingId(null);
    setDraft(emptyDraft);
    setSearchLeft('');
    setSearchRight('');
  }

  function openEditDeal(deal) {
    setEditingId(deal.id);
    setDraft({ brand: deal.brand, picks_in: deal.picks_in.slice(), picks_out: deal.picks_out.slice(), cashIn: deal.cashIn, cashOut: deal.cashOut });
    setSearchLeft('');
    setSearchRight('');
  }

  function cancelEdit() {
    setEditingId(null);
    setDraft(emptyDraft);
  }

  function saveDeal() {
    if (!draft.brand) return;
    if (editingId !== null) {
      // Update existing
      setDeals(function(prev){ return prev.map(function(d){ return d.id === editingId ? { ...draft, id: editingId } : d; }); });
    } else {
      // Add new
      var newId = String(Date.now());
      setDeals(function(prev){ return [...prev, { ...draft, id: newId }]; });
    }
    setEditingId(null);
    setDraft(emptyDraft);
    setSearchLeft('');
    setSearchRight('');
  }

  function deleteDeal(id) {
    setDeals(function(prev){ return prev.filter(function(d){ return d.id !== id; }); });
    if (editingId === id) {
      setEditingId(null);
      setDraft(emptyDraft);
    }
  }

  function toggleLeft(w) {
    setDraft(function(prev) {
      var picks = prev.picks_in || [];
      var wid = String(w.wrestler_id);
      var exists = picks.find(function(x){ return String(x.wrestler_id) === wid; });
      return { ...prev, picks_in: exists ? picks.filter(function(x){ return String(x.wrestler_id) !== wid; }) : picks.length < 3 ? [...picks, w] : picks };
    });
  }

  function toggleRight(w) {
    setDraft(function(prev) {
      var picks = prev.picks_out || [];
      var wid = String(w.wrestler_id);
      var exists = picks.find(function(x){ return String(x.wrestler_id) === wid; });
      return { ...prev, picks_out: exists ? picks.filter(function(x){ return String(x.wrestler_id) !== wid; }) : picks.length < 3 ? [...picks, w] : picks };
    });
  }

  // ── Final roster ───────────────────────────────────────────────────
  const finalRoster = useMemo(function(){
    return sortWrestlersByName(rosterAfterDeals, 'asc');
  }, [rosterAfterDeals]);

  // ── All arrivals & departures ──────────────────────────────────────
  const allArrivals = useMemo(function(){
    var arr = [];
    deals.forEach(function(d) {
      var rObj = rivals.find(function(r){ return r.brand === d.brand; });
      (d.picks_in || []).forEach(function(w){
        arr.push({
          wrestler: w,
          fromBrand: d.brand,
          fromColor: (rObj && (rObj.color || rObj.brandColor)) || getBrandColor(d.brand) || '#888',
          fromLogoUrl: (rObj && rObj.brandLogo) || null,
        });
      });
    });
    return arr;
  }, [deals, rivals]);

  const allDepartures = useMemo(function(){
    var arr = [];
    deals.forEach(function(d) {
      var rObj = rivals.find(function(r){ return r.brand === d.brand; });
      (d.picks_out || []).forEach(function(w){
        arr.push({
          wrestler: w,
          toBrand: d.brand,
          toColor: (rObj && (rObj.color || rObj.brandColor)) || getBrandColor(d.brand) || '#888',
          toLogoUrl: (rObj && rObj.brandLogo) || null,
        });
      });
    });
    return arr;
  }, [deals, rivals]);

  // ── Confirm ────────────────────────────────────────────────────────
  function handleConfirm() {
    var cashDelta = 0;
    var shakeUpRecap = deals.map(function(d) {
      cashDelta += Number(d.cashIn || 0) - Number(d.cashOut || 0);
      return {
        brand: d.brand,
        picks_in:  (d.picks_in  || []).map(function(w){ return { wrestler_id: w.wrestler_id, name: getWrestlerName(w, 'display') }; }),
        picks_out: (d.picks_out || []).map(function(w){ return { wrestler_id: w.wrestler_id, name: getWrestlerName(w, 'display') }; }),
        cashIn: Number(d.cashIn || 0), cashOut: Number(d.cashOut || 0),
      };
    });

    var currentHistory = (gameData && gameData.seasonHistory) || [];
    var updatedHistory = currentHistory.slice();
    if (updatedHistory.length > 0) {
      updatedHistory[updatedHistory.length - 1] = { ...updatedHistory[updatedHistory.length - 1], shakeUp: shakeUpRecap };
    }

    var cleanRoster = finalRoster.map(function(w){ var c = { ...w }; delete c._newArrival; return c; });
    onComplete({ ...gameData, roster: cleanRoster, budget: budget + cashDelta, seasonHistory: updatedHistory, _shakeUpDone: true });
  }

  // ── Draft brand color + logos ────────────────────────────────────
  const draftRivalObj   = rivals.find(function(r){ return r.brand === draft.brand; });
  const draftRivalColor = draftRivalObj ? (draftRivalObj.color || draftRivalObj.brandColor || getBrandColor(draft.brand) || '#e74c3c') : (getBrandColor(draft.brand) || '#e74c3c');
  // Custom logo for my own brand (set when brand is a custom brand)
  const myBrandLogoUrl  = (gameData && gameData.brandLogo) || null;

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(4,2,0,0.97)', display: 'flex', flexDirection: 'column', fontFamily: "'Monda', sans-serif" }}>
      <style>{css}</style>
      <style>{WrestlerCardStyles}</style>

      {/* ── TOP BAR ───────────────────────────────────────────────── */}
      <div style={{ flexShrink: 0, padding: '14px 24px', borderBottom: '1px solid rgba(255,255,255,0.07)', background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div>
            <div style={{ ...S.mono, fontSize: '0.38rem', letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginBottom: '2px' }}>POST-PLE EVENT</div>
            <div style={{ ...S.anton, fontSize: '1.4rem', letterSpacing: '3px', color: brandColor }}>SHAKE-UP</div>
          </div>
          <BrandPill brand={myBrand} color={brandColor} logoUrl={myBrandLogoUrl} />
        </div>

        {/* Step indicators */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {[{ key: 'deals', label: 'DEALS' }, { key: 'recap', label: 'RECAP' }].map(function(s, i) {
            var isActive = step === s.key;
            var isDone   = s.key === 'deals' && step === 'recap';
            return (
              <React.Fragment key={s.key}>
                {i > 0 && <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.5rem' }}>›</span>}
                <div style={{ ...S.mono, fontSize: '0.38rem', letterSpacing: '1.5px', padding: '5px 14px', borderRadius: '5px', color: isActive ? brandColor : isDone ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.25)', background: isActive ? brandColor + '18' : 'transparent', border: '1px solid ' + (isActive ? brandColor + '55' : 'transparent') }}>
                  {isDone && '✓ '}{s.label}
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {/* Budget + skip */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(46,204,113,0.08)', border: '1px solid rgba(46,204,113,0.25)', borderRadius: '5px', padding: '5px 12px' }}>
            <span style={{ ...S.mono, fontSize: '0.36rem', color: 'rgba(255,255,255,0.4)', letterSpacing: '1px' }}>BUDGET</span>
            <span style={{ ...S.anton, fontSize: '0.75rem', color: '#2ecc71', letterSpacing: '1px' }}>${budget.toLocaleString('en-US')}</span>
          </div>
          <button onClick={onSkip} style={{ ...S.mono, fontSize: '0.4rem', letterSpacing: '1px', background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.3)', padding: '6px 16px', borderRadius: '5px', cursor: 'pointer' }}>SKIP ALL</button>
        </div>
      </div>

      {/* ── CONTENT ───────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>

        {/* ══ STEP: DEALS ══════════════════════════════════════════ */}
        {step === 'deals' && (
          <>
            {/* LEFT PANEL — deals list */}
            <div style={{ width: '260px', flexShrink: 0, borderRight: '1px solid rgba(255,255,255,0.07)', display: 'flex', flexDirection: 'column', background: 'rgba(0,0,0,0.4)' }}>
              {/* Header */}
              <div style={{ flexShrink: 0, padding: '12px 14px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ ...S.label }}>ALL DEALS</span>
                <span style={{ ...S.mono, fontSize: '0.38rem', color: 'rgba(255,255,255,0.3)' }}>{deals.length} deal{deals.length !== 1 ? 's' : ''}</span>
              </div>

              {/* Deals list */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '10px', display: 'flex', flexDirection: 'column', gap: '7px' }}>
                {deals.map(function(deal, i) {
                  return (
                    <DealCard
                      key={deal.id}
                      deal={deal}
                      index={i}
                      isActive={editingId === deal.id}
                      onClick={function(){ openEditDeal(deal); }}
                      onDelete={function(){ deleteDeal(deal.id); }}
                      rivalsList={rivals}
                    />
                  );
                })}

                {/* Add deal button */}
                <button
                  onClick={openNewDeal}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', padding: '9px 14px', borderRadius: '7px', border: '1px dashed rgba(255,255,255,0.2)', background: 'transparent', color: 'rgba(255,255,255,0.35)', cursor: 'pointer', ...S.mono, fontSize: '0.42rem', letterSpacing: '1px', transition: 'border-color 0.15s, color 0.15s' }}
                  onMouseEnter={function(e){ e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
                  onMouseLeave={function(e){ e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; e.currentTarget.style.color = 'rgba(255,255,255,0.35)'; }}
                >
                  <span style={{ fontSize: '0.7rem', lineHeight: 1 }}>+</span>
                  ADD A DEAL
                </button>

                {deals.length === 0 && editingId === null && draft.brand === '' && (
                  <div style={{ ...S.mono, fontSize: '0.42rem', color: 'rgba(255,255,255,0.2)', textAlign: 'center', padding: '20px 0', letterSpacing: '0.5px' }}>
                    Click "Add a deal" to start
                  </div>
                )}
              </div>

              {/* See recap */}
              <div style={{ flexShrink: 0, padding: '12px 14px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                <button
                  onClick={function(){ setStep('recap'); }}
                  style={{ ...S.anton, fontSize: '0.6rem', letterSpacing: '2px', width: '100%', padding: '11px', borderRadius: '6px', background: '#2ecc7118', border: '2px solid #2ecc71', color: '#2ecc71', cursor: 'pointer' }}
                >
                  SEE RECAP →
                </button>
              </div>
            </div>

            {/* RIGHT PANEL — editor */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

              {/* Editor header */}
              <div style={{ flexShrink: 0, padding: '12px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', gap: '14px', background: 'rgba(0,0,0,0.3)' }}>
                <span style={{ ...S.label }}>DEAL WITH</span>
                <BrandDropdown
                  value={draft.brand}
                  options={rivals.map(function(r){ return r.brand; })}
                  onChange={function(brand){ setDraft(function(p){ return { ...p, brand: brand, picks_in: [], picks_out: [] }; }); setSearchLeft(''); setSearchRight(''); }}
                  placeholder="— Select a brand —"
                  rivalsList={rivals}
                />
                <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px' }}>
                  {(editingId !== null || draft.brand !== '') && (
                    <button onClick={cancelEdit} style={{ ...S.mono, fontSize: '0.44rem', letterSpacing: '1px', background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.35)', padding: '9px 18px', borderRadius: '5px', cursor: 'pointer' }}>CANCEL</button>
                  )}
                  <button
                    onClick={saveDeal}
                    disabled={!draft.brand}
                    style={{ ...S.anton, fontSize: '0.6rem', letterSpacing: '2px', background: draft.brand ? brandColor + '18' : 'rgba(255,255,255,0.04)', border: '1px solid ' + (draft.brand ? brandColor : 'rgba(255,255,255,0.1)'), color: draft.brand ? brandColor : 'rgba(255,255,255,0.2)', padding: '9px 22px', borderRadius: '6px', cursor: draft.brand ? 'pointer' : 'not-allowed' }}
                  >
                    {editingId !== null ? 'UPDATE DEAL ✓' : 'SAVE DEAL ✓'}
                  </button>
                </div>
              </div>

              {draft.brand ? (
                <>
                  {/* Two-column picker */}
                  <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                    <PickerColumn
                      label={draft.brand.toUpperCase() + ' — AVAILABLE'}
                      brandName={draft.brand}
                      accentColor={draftRivalColor}
                      logoUrl={draftRivalObj ? (draftRivalObj.brandLogo || null) : null}
                      wrestlers={filteredLeft}
                      picks={draft.picks_in}
                      onToggle={toggleLeft}
                      search={searchLeft}
                      onSearch={setSearchLeft}
                      maxPicks={3}
                      emptyMsg="No superstars available"
                    />
                    <div style={{ width: '1px', background: 'rgba(255,255,255,0.06)', flexShrink: 0 }} />
                    <PickerColumn
                      label={myBrand.toUpperCase() + ' — MY ROSTER'}
                      brandName={myBrand}
                      accentColor={brandColor}
                      logoUrl={myBrandLogoUrl}
                      wrestlers={filteredRight}
                      picks={draft.picks_out}
                      onToggle={toggleRight}
                      search={searchRight}
                      onSearch={setSearchRight}
                      maxPicks={3}
                      emptyMsg="Your roster is empty"
                    />
                  </div>

                  {/* Cash row */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px', borderTop: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ ...S.label, marginBottom: '4px' }}>{'CASH FROM ' + draft.brand.toUpperCase()}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '5px', padding: '6px 10px' }}>
                        <span style={{ ...S.mono, fontSize: '0.5rem', color: 'rgba(255,255,255,0.3)' }}>$</span>
                        <input type="number" value={draft.cashIn || ''} onChange={function(e){ setDraft(function(p){ return { ...p, cashIn: e.target.value }; }); }} placeholder="0"
                          style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.85rem', background: 'transparent', border: 'none', color: '#fff', outline: 'none', flex: 1, letterSpacing: '1px', width: '100%' }} />
                      </div>
                    </div>
                    <span style={{ ...S.mono, fontSize: '0.6rem', color: 'rgba(255,255,255,0.2)', flexShrink: 0 }}>⇄</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ ...S.label, marginBottom: '4px' }}>{'CASH TO ' + draft.brand.toUpperCase()}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '5px', padding: '6px 10px' }}>
                        <span style={{ ...S.mono, fontSize: '0.5rem', color: 'rgba(255,255,255,0.3)' }}>$</span>
                        <input type="number" value={draft.cashOut || ''} onChange={function(e){ setDraft(function(p){ return { ...p, cashOut: e.target.value }; }); }} placeholder="0"
                          style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.85rem', background: 'transparent', border: 'none', color: '#fff', outline: 'none', flex: 1, letterSpacing: '1px', width: '100%' }} />
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                /* Empty state — no brand selected yet */
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                  <div style={{ ...S.anton, fontSize: '1rem', letterSpacing: '3px', color: 'rgba(255,255,255,0.08)' }}>SELECT A BRAND</div>
                  <div style={{ ...S.mono, fontSize: '0.46rem', color: 'rgba(255,255,255,0.2)', letterSpacing: '1px' }}>Choose a rival brand above to set up a deal</div>
                </div>
              )}
            </div>
          </>
        )}

        {/* ══ STEP: RECAP ══════════════════════════════════════════ */}
        {step === 'recap' && (
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px' }}>

            {/* Stats */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '28px' }}>
              {[
                { val: allArrivals.length,   label: 'JOINING',      color: '#2ecc71' },
                { val: allDepartures.length, label: 'LEAVING',      color: '#e74c3c' },
                { val: finalRoster.length,   label: 'TOTAL ROSTER', color: brandColor },
              ].map(function(s) {
                return (
                  <div key={s.label} style={{ flex: 1, background: s.color + '10', border: '1px solid ' + s.color + '33', borderRadius: '8px', padding: '14px 18px', textAlign: 'center' }}>
                    <div style={{ ...S.anton, fontSize: '1.8rem', color: s.color, letterSpacing: '2px' }}>{s.val}</div>
                    <div style={{ ...S.mono, fontSize: '0.4rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '1.5px', marginTop: '4px' }}>{s.label}</div>
                  </div>
                );
              })}
            </div>

            {/* Arrivals */}
            {allArrivals.length > 0 && (
              <div style={{ marginBottom: '28px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                  <span style={{ ...S.anton, fontSize: '0.9rem', letterSpacing: '2px', color: '#2ecc71' }}>ARRIVALS</span>
                  <div style={{ flex: 1, height: '1px', background: 'rgba(46,204,113,0.2)' }} />
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {allArrivals.map(function(entry, i) {
                    return <RecapCard key={String(entry.wrestler.wrestler_id) + '-in-' + i} wrestler={entry.wrestler} brandName={entry.fromBrand} brandLogoUrl={entry.fromLogoUrl} badgeColor={entry.fromColor} brandColor={brandColor} direction="in" />;
                  })}
                </div>
              </div>
            )}

            {/* Departures */}
            {allDepartures.length > 0 && (
              <div style={{ marginBottom: '28px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                  <span style={{ ...S.anton, fontSize: '0.9rem', letterSpacing: '2px', color: '#e74c3c' }}>DEPARTURES</span>
                  <div style={{ flex: 1, height: '1px', background: 'rgba(231,76,60,0.2)' }} />
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {allDepartures.map(function(entry, i) {
                    return <RecapCard key={String(entry.wrestler.wrestler_id) + '-out-' + i} wrestler={entry.wrestler} brandName={entry.toBrand} brandLogoUrl={entry.toLogoUrl} badgeColor={entry.toColor} brandColor='#e74c3c' direction="out" />;
                  })}
                </div>
              </div>
            )}

            {/* Updated roster */}
            <div style={{ marginBottom: '28px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                <span style={{ ...S.anton, fontSize: '0.9rem', letterSpacing: '2px', color: brandColor }}>UPDATED ROSTER</span>
                <div style={{ flex: 1, height: '1px', background: brandColor + '33' }} />
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {finalRoster.map(function(w) {
                  var isNew = !!w._newArrival;
                  return (
                    <div key={String(w.wrestler_id)} className={isNew ? 'su-new-glow' : ''} style={{ borderRadius: '8px' }}>
                      <WrestlerCard wrestler={w} variant="preview" size={{ width: 110, height: 230, renderHeight: 158 }} brandColor={brandColor} hideXP={true} hideStats={false} hasSynergy={false} isChampionGlow={false} showActionButtons={false} />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Confirm */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.07)', marginBottom: '8px' }}>
              <button onClick={function(){ setStep('deals'); }} style={{ ...S.mono, fontSize: '0.44rem', letterSpacing: '1px', background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.35)', padding: '9px 20px', borderRadius: '5px', cursor: 'pointer' }}>← BACK</button>
              {(() => {
                var totalCashDelta = deals.reduce(function(sum, d) { return sum + Number(d.cashIn || 0) - Number(d.cashOut || 0); }, 0);
                var wouldBeNegative = budget + totalCashDelta < 0;
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                    {wouldBeNegative && (
                      <div style={{ ...S.mono, fontSize: '0.38rem', color: '#e74c3c', letterSpacing: '0.5px' }}>
                        ⚠ Insufficient budget — remove a cash deal first.
                      </div>
                    )}
                    <button onClick={handleConfirm} disabled={wouldBeNegative}
                      style={{ ...S.anton, fontSize: '0.75rem', letterSpacing: '3px', background: wouldBeNegative ? 'rgba(255,255,255,0.03)' : '#2ecc7118', border: '2px solid ' + (wouldBeNegative ? 'rgba(255,255,255,0.1)' : '#2ecc71'), color: wouldBeNegative ? 'rgba(255,255,255,0.2)' : '#2ecc71', padding: '14px 44px', borderRadius: '7px', cursor: wouldBeNegative ? 'not-allowed' : 'pointer', transition: 'all 0.15s' }}>
                      CONFIRM SHAKE-UP ✓
                    </button>
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
